/**
 * What the welcome tour says, worked out from what the reader actually holds.
 *
 * Pure on purpose: the tour's only interesting logic is "which lines does THIS
 * person see", and that is worth testing without mounting a dialog.
 *
 * ## Why this derives from capabilities rather than reusing ROLE_DESCRIPTIONS
 *
 * `ROLE_DESCRIPTIONS` in ./staff-roles exists for the Admins dialog, where
 * someone is deciding whether to GRANT a role - hence "Grant sparingly", which
 * is the wrong sentence to read about yourself on your first day. Its own
 * comment records that it had already gone stale once against
 * ROLE_CAPABILITIES.
 *
 * So the tour reads the capability set instead: the same table the console
 * gates its buttons on and the same one the Callables enforce. A role whose
 * grants change says something different here the moment it changes, with
 * nobody remembering to edit prose.
 */
import { WRITE_CAPABILITIES, type Capability } from './staff-roles';

/** One line of "here is what you can do", and the capability that earns it. */
interface Highlight {
  readonly needs: readonly Capability[];
  readonly line: string;
}

/**
 * Ordered most-used first, not most-powerful first: this is a welcome, so the
 * top line should be the thing they are most likely to open the console for.
 *
 * Each entry needs ANY of its capabilities, because a few lines describe one
 * job that spans two of them ("accounts and invitations"). Splitting those
 * into a line each would be accurate and would read like a permissions dump.
 */
const HIGHLIGHTS: readonly Highlight[] = [
  { needs: ['partners.write'], line: 'Add partners, and edit their details, schools and contacts' },
  { needs: ['licenses.write'], line: 'Set up and renew licences' },
  { needs: ['accounts.write', 'invites.write'], line: 'Invite people, and manage their accounts' },
  { needs: ['memberships.write'], line: 'Move people between partners, and deactivate them' },
  { needs: ['support.write'], line: 'Triage support requests - status, owner and notes' },
  {
    needs: ['governance.write'],
    line: "Name a partner's Lead Administrator - the person who decides V3",
  },
  { needs: ['activity.read'], line: 'Read the activity log - who changed what, and when' },
  { needs: ['staff.manage'], line: 'Grant and revoke console access for other admins' },
  { needs: ['partners.delete', 'accounts.delete'], line: 'Delete partners and accounts' },
  { needs: ['mockdata.write'], line: 'Seed and remove demo data' },
  { needs: ['launch.populate'], line: 'Run the launch import' },
];

/**
 * The floor. Reads are open to every staff role at the rules level (see the
 * long note in ./staff-roles), so this is true for anyone who can sign in -
 * and without it a read-only Support admin's step would list nothing at all
 * and read as "you have no access", which is both discouraging and false.
 */
const READ_ONLY_FLOOR = 'Look up any partner, account or invitation';

/**
 * The lines to show this person, in order. Never empty: a signed-in admin can
 * always at least read.
 */
export function onboardingHighlights(capabilities: ReadonlySet<Capability>): string[] {
  const lines = HIGHLIGHTS.filter((h) => h.needs.some((c) => capabilities.has(c))).map(
    (h) => h.line,
  );
  return lines.length > 0 ? [READ_ONLY_FLOOR, ...lines] : [READ_ONLY_FLOOR];
}

/**
 * Every write capability that no highlight mentions.
 *
 * Exported for the spec rather than for the app. A capability added to the
 * table with no line here is invisible to the tour - not a broken build, not a
 * failing assertion anywhere else, just a person told slightly less than the
 * truth about their own access. This is the only thing that notices.
 */
export function unmentionedWriteCapabilities(): Capability[] {
  const mentioned = new Set(HIGHLIGHTS.flatMap((h) => h.needs));
  return WRITE_CAPABILITIES.filter((c) => !mentioned.has(c));
}

/**
 * The search chord to show, per platform.
 *
 * The palette binds `keydown.meta.k` AND `keydown.control.k`, so both chords
 * genuinely work everywhere - this only picks which one to PRINT. Showing a
 * Mac user Ctrl K would send them to the one that also works but that nothing
 * else on their machine uses.
 *
 * Takes the platform string rather than reading navigator, so the branch is
 * testable and so a server-render (none today) cannot crash on a missing
 * global.
 */
export function searchChord(platform: string): string {
  return /mac|iphone|ipad|ipod/i.test(platform) ? '⌘ K' : 'Ctrl K';
}

/** The browser's best guess at the platform, for {@link searchChord}. */
export function currentPlatform(): string {
  if (typeof navigator === 'undefined') return '';
  // userAgentData is the non-deprecated source; navigator.platform is the
  // fallback every current browser still populates.
  const data = (navigator as { userAgentData?: { platform?: string } }).userAgentData;
  return data?.platform ?? navigator.platform ?? '';
}

/** Everything that has to be true before the welcome tour may open itself. */
export interface OnboardingGate {
  /** The server's stamp: a number means they have finished it before. */
  readonly onboardedAt: number | null;
  /** Has the onboarding state actually been fetched? Before it has, `null`
   *  above means "unknown", not "new" - showing the tour on that would flash it
   *  at everyone, once, on every load. */
  readonly loaded: boolean;
  /** Auth settled AND roles present - step 2 lists roles, so an empty set is
   *  not a tour worth showing. */
  readonly isStaff: boolean;
}

/**
 * Should this admin be sent to the tour?
 *
 * Deliberately not `!onboardedAt`. Two of these three are about whether the
 * answer is KNOWN yet, and reading the stamp alone gets both wrong: `null`
 * before the fetch returns means "not asked", not "new", and a fetch that
 * FAILED must not reroute - the same failure would lose the stamp written on
 * the way out, so the reader would bounce back here on every navigation with
 * no way through. See onboarding-guard.ts for why that direction was chosen.
 */
export function shouldShowOnboarding(gate: OnboardingGate): boolean {
  return gate.loaded && gate.isStaff && gate.onboardedAt === null;
}
